import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { VisualShape } from 'types'
import MultimodalShowcase from './MultimodalShowcase'
import { HeroSection } from './landing/HeroSection'
import { WorkshopPrepSection } from './landing/WorkshopPrepSection'
import { ServicesSection } from './landing/ServicesSection'
import { WorkshopsSection } from './landing/WorkshopsSection'
import { ContactSection } from './landing/ContactSection'
import { LandingHeader } from './landing/LandingHeader'
import { LandingFooter } from './landing/LandingFooter'
import * as TOKENS from './chat/design-tokens'
import { useScrollReveal, REVEAL } from '../hooks/useScrollReveal'
import { setLandingScrollProgress, resetLandingScrollProgress } from '../lib/landing-canvas-scroll.js'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { usePageMeta } from '../hooks/ui/usePageMeta.js'
import { PAGE_TITLE } from '../data/workshop-packages.js'
import { useLandingContent } from '../hooks/i18n/usePageContent.js'

/* ── Types ─────────────────────────────────────────────────────────── */

interface LandingPageProps {
  onStartChat: () => void
  onSectionChange?: (shape: VisualShape) => void
  onAdminAccess?: () => void
  onAboutAccess?: () => void
}

/* ── Static data ────────────────────────────────────────────────────── */

/* ── Shared atoms ───────────────────────────────────────────────────── */

/** Standard section shell — matches all child sections */
const sectionShell = `w-full scroll-mt-24 px-6 py-20 md:px-12 border-t ${TOKENS.BORDER.default} bg-transparent`

const eyebrow = `${TOKENS.TEXT_SIZE.xs} font-mono uppercase tracking-[0.25em] ${TOKENS.TEXT.muted}`

/** Capability card — flat, bordered, shimmer-on-hover */
const capabilityCard = `
  group relative overflow-hidden p-5 border rounded-none
  ${TOKENS.SURFACE_ROLES.raised} ${TOKENS.BORDER.default}
  transition-colors duration-150
  hover:border-[#c95f3a]/40 hover:bg-muted
`

/** About card — same flat treatment */
const aboutCard = `
  group relative overflow-hidden flex flex-col gap-4 p-6 border rounded-none
  ${TOKENS.SURFACE_ROLES.raised} ${TOKENS.BORDER.default}
  transition-colors duration-150
  hover:border-foreground/20 hover:bg-muted
`

/* ── Component ─────────────────────────────────────────────────────── */

const LandingPage: React.FC<LandingPageProps> = ({
  onStartChat,
  onSectionChange,
  onAdminAccess,
  onAboutAccess,
}) => {
  const location = useLocation()
  const { t, i18n } = useTranslation('marketing')
  const titleLang = i18n.language?.startsWith('nb') ? 'nb' : 'en'
  const path = location.pathname.replace(/\/+$/, '') || '/'
  usePageMeta(PAGE_TITLE.landing[titleLang], t('landing.description'), 'https://www.farzadbayat.com/', {
    ogTitle: PAGE_TITLE.landing[titleLang],
    ogUrl: 'https://www.farzadbayat.com/',
    ogImage: 'https://www.farzadbayat.com/farzad-bayat.jpg',
    keywords: t('landing.keywords'),
    noindex: path === '/landing',
  })
  const content = useLandingContent()
  const { agent, aboutSection, validation } = content

  /* Refs */
  const aboutSectionId = useId()
  const aboutHeadingId = useId()
  const heroRef = useRef<HTMLDivElement>(null)
  const showcaseRef = useRef<HTMLElement>(null)
  const servicesRef = useRef<HTMLElement>(null)
  const workshopsRef = useRef<HTMLElement>(null)
  const aboutRef = useRef<HTMLElement>(null)
  const contactRef = useRef<HTMLElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  /* State */
  const multimodalRef = useRef<HTMLElement>(null)

  /* ── Scroll reveal hooks per section ──────────────────────────────── */
  const showcaseReveal = useScrollReveal()
  const multimodalReveal = useScrollReveal()
  const aboutReveal = useScrollReveal()

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [hoveredCard, setHoveredCard] = useState<VisualShape | null>(null)
  const [formState, setFormState] = useState({
    name: '',
    company: '',
    email: '',
    interest: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  /* ── Handlers ────────────────────────────────────────────────────── */

  const handleFormChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target
      setFormState(prev => ({ ...prev, [name]: value }))
    },
    []
  )

  const validateForm = useCallback(() => {
    const errors: Record<string, string> = {}
    if (!formState.name.trim()) errors.name = validation.nameRequired
    if (!formState.email.trim()) {
      errors.email = validation.emailRequired
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      errors.email = validation.emailInvalid
    }
    return errors
  }, [formState.name, formState.email, validation.emailInvalid, validation.emailRequired, validation.nameRequired])

  const handleFormSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const errors = validateForm()
      setFieldErrors(errors)
      if (Object.keys(errors).length > 0) return

      setIsSubmitting(true)
      setSubmitStatus('idle')
      try {
        const res = await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formState),
        })
        if (!res.ok) throw new Error('Request failed')
        setSubmitStatus('success')
        setFormState({ name: '', company: '', email: '', interest: '', message: '' })
        setFieldErrors({})
      } catch {
        setSubmitStatus('error')
      } finally {
        setIsSubmitting(false)
      }
    },
    [formState, validateForm]
  )

  const scrollToSection = useCallback((ref: React.RefObject<HTMLElement>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setIsMobileMenuOpen(false)
  }, [])

  const scrollToShowcase = useCallback(() => scrollToSection(showcaseRef), [scrollToSection])
  const scrollToServices = useCallback(() => scrollToSection(servicesRef), [scrollToSection])
  const scrollToAbout = useCallback(() => scrollToSection(aboutRef), [scrollToSection])
  const scrollToContact = useCallback(() => scrollToSection(contactRef), [scrollToSection])

  const handleCardHover = useCallback(
    (shape: VisualShape) => {
      setHoveredCard(shape)
      onSectionChange?.(shape)
    },
    [onSectionChange]
  )

  const handleCardLeave = useCallback(() => {
    setHoveredCard(null)
    onSectionChange?.('constellation')
  }, [onSectionChange])

  /* ── IntersectionObserver ────────────────────────────────────────── */
  useEffect(() => {
    if (!onSectionChange) return
    const map = new Map<Element, VisualShape>()
    if (heroRef.current) map.set(heroRef.current, 'constellation')
    if (showcaseRef.current) map.set(showcaseRef.current, 'constellation')
    if (servicesRef.current) map.set(servicesRef.current, 'grid')
    if (workshopsRef.current) map.set(workshopsRef.current, 'brain')
    if (aboutRef.current) map.set(aboutRef.current, 'dna')
    if (contactRef.current) map.set(contactRef.current, 'hourglass')

    const observer = new IntersectionObserver(
      entries => {
        for (const e of entries) {
          if (!e.isIntersecting) continue
          const shape = map.get(e.target)
          if (shape) onSectionChange(shape)
        }
      },
      { threshold: 0.25 }
    )
    for (const el of map.keys()) observer.observe(el)
    return () => observer.disconnect()
  }, [onSectionChange])

  /* ── Landing scroll → canvas parallax / wave boost ─────────────── */
  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return
    const syncScroll = () => {
      const max = Math.max(1, el.scrollHeight - el.clientHeight)
      setLandingScrollProgress(el.scrollTop / max)
    }
    syncScroll()
    el.addEventListener('scroll', syncScroll, { passive: true })
    return () => {
      el.removeEventListener('scroll', syncScroll)
      resetLandingScrollProgress()
    }
  }, [])

  /* ── Body scroll lock ────────────────────────────────────────────── */
  useEffect(() => {
    // Lock body scroll when mobile menu is open
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    // Ensure cleanup on unmount
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileMenuOpen])

  /* ── Render ──────────────────────────────────────────────────────── */
  return (
    <div
      ref={scrollContainerRef}
      className={`
        relative h-[100dvh] w-full overflow-y-auto scroll-smooth bg-transparent
        pt-20 text-foreground selection:bg-[#c95f3a]/20
        transition-colors duration-150 custom-scrollbar
      `}
    >

      {/* Header */}
      <LandingHeader
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        scrollToServices={scrollToServices}
        handleAboutNavigation={onAboutAccess ?? scrollToAbout}
        scrollToContact={scrollToContact}
        onStartChat={onStartChat}
      />
      <HeroSection
        onStartChat={onStartChat}
        scrollToShowcase={scrollToShowcase}
        heroRef={heroRef}
      />

      <WorkshopPrepSection surface="landing" />

      {/* ── Agent showcase ──────────────────────────────────────── */}
      <section ref={showcaseRef} className={sectionShell}>
        <div
          ref={showcaseReveal.ref as React.RefObject<HTMLDivElement>}
          className={`relative z-10 mx-auto flex max-w-6xl flex-col gap-12 ${REVEAL.transition} ${showcaseReveal.isRevealed ? REVEAL.visible : REVEAL.hidden}`}
        >
          {/* Section header */}
          <div className={`flex flex-col gap-4 ${REVEAL.transition} ${showcaseReveal.isRevealed ? REVEAL.visible : REVEAL.hidden} ${REVEAL.delay100}`}>
            <span className={eyebrow}>{agent.eyebrow}</span>
            <h2 className={`text-3xl font-bold tracking-tight md:text-4xl ${TOKENS.TEXT.primary}`}>
              {agent.title.prefix}{' '}
              <span className="font-mono">
                F.B<span className={TOKENS.BRAND.slash}>/c</span>
              </span>{' '}
              {agent.title.suffix}
            </h2>
            <p className={`max-w-3xl text-lg font-light leading-relaxed ${TOKENS.TEXT.secondary}`}>
              {agent.body}
            </p>
            <p
              className={`${TOKENS.TEXT_SIZE.xs} font-medium italic ${TOKENS.TEXT.muted} md:hidden`}
            >
              {agent.tapHint}
            </p>
            <p
              className={`${TOKENS.TEXT_SIZE.xs} font-medium italic ${TOKENS.TEXT.muted} hidden md:block`}
            >
              {agent.hoverHint}
            </p>
          </div>

          {/* Capability cards */}
          <ul className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {agent.capabilityCards.map(cap => (
              <li
                key={cap.label}
                className={`
                  ${capabilityCard}
                  ${hoveredCard === cap.shape ? 'border-[#c95f3a]/40 bg-muted' : ''}
                `}
                onMouseEnter={() => handleCardHover(cap.shape as VisualShape)}
                onMouseLeave={handleCardLeave}
              >
                <span className={TOKENS.INTERACTION.shimmer} />
                <span
                  className={`
                    mb-1 block ${TOKENS.TEXT_SIZE.xs} font-mono uppercase tracking-wider
                    ${TOKENS.TEXT.muted}
                    transition-colors duration-150 group-hover:text-[#c95f3a]
                  `}
                >
                  {cap.label}
                </span>
                <span className={`text-xs ${TOKENS.TEXT.secondary}`}>{cap.sub}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Multimodal showcase */}
      <section
        ref={multimodalRef}
      >
        <div
          ref={multimodalReveal.ref as React.RefObject<HTMLDivElement>}
          className={`${REVEAL.transition} ${multimodalReveal.isRevealed ? REVEAL.visible : REVEAL.hidden}`}
        >
          <MultimodalShowcase
            onStartChat={onStartChat}
            {...(onSectionChange ? { onShapeChange: onSectionChange } : {})}
          />
        </div>
      </section>

      {/* Services */}
      <ServicesSection servicesRef={servicesRef} />

      {/* Workshops */}
      <WorkshopsSection workshopsRef={workshopsRef} />

      {/* ── About ───────────────────────────────────────────────── */}
      <section
        ref={aboutRef}
        id={aboutSectionId}
        aria-labelledby={aboutHeadingId}
        className={`${sectionShell} relative overflow-hidden`}
      >
        {/* Ambient glow — pointer-events off, purely decorative */}
        <div
          className="pointer-events-none absolute right-0 top-12 h-96 w-96 rounded-full bg-[#c95f3a]/5 blur-[100px]"
          aria-hidden="true"
        />

        <div
          ref={aboutReveal.ref as React.RefObject<HTMLDivElement>}
          className={`relative z-10 mx-auto flex max-w-6xl flex-col gap-16 ${REVEAL.transition} ${aboutReveal.isRevealed ? REVEAL.visible : REVEAL.hidden}`}
        >
          <div className={`flex max-w-3xl flex-col gap-4 ${REVEAL.transition} ${aboutReveal.isRevealed ? REVEAL.visible : REVEAL.hidden} ${REVEAL.delay100}`}>
            <span className={eyebrow}>{aboutSection.eyebrow}</span>
            <h2
              id={aboutHeadingId}
              className={`text-3xl font-bold tracking-tight md:text-4xl ${TOKENS.TEXT.primary}`}
            >
              {aboutSection.title}
            </h2>
            <p className={`text-lg font-light leading-relaxed ${TOKENS.TEXT.secondary}`}>
              {aboutSection.body}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {aboutSection.cards.map(item => (
              <div key={item.title} className={aboutCard}>
                <span className={TOKENS.INTERACTION.shimmer} />
                <span
                  className={`${TOKENS.TEXT_SIZE.xs} font-mono uppercase tracking-widest ${TOKENS.TEXT.muted}`}
                >
                  {aboutSection.coreFocusLabel}
                </span>
                <h3 className={`text-lg font-bold tracking-tight ${TOKENS.TEXT.primary}`}>
                  {item.title}
                </h3>
                <p className={`text-sm font-light leading-relaxed ${TOKENS.TEXT.secondary}`}>
                  {item.body}
                </p>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-3 pt-2 sm:flex-row">
            <button
              type="button"
              onClick={() => onStartChat()}
              className={`group relative w-full overflow-hidden sm:w-auto ${TOKENS.PATTERNS.marketingPrimaryLarge}`}
            >
              <span className={TOKENS.INTERACTION.shimmer} />
              <span className="relative">{aboutSection.ctas.primary}</span>
            </button>
            <button
              type="button"
              onClick={scrollToContact}
              className={`group relative w-full overflow-hidden sm:w-auto ${TOKENS.PATTERNS.marketingSecondaryLarge}`}
            >
              <span className={TOKENS.INTERACTION.shimmer} />
              <span className="relative">{aboutSection.ctas.scheduleCall}</span>
            </button>
          </div>
        </div>
      </section>

      {/* Contact */}
      <ContactSection
        contactRef={contactRef}
        formState={formState}
        handleFormChange={handleFormChange}
        handleFormSubmit={(e: React.FormEvent) => void handleFormSubmit(e)}
        isSubmitting={isSubmitting}
        submitStatus={submitStatus}
        fieldErrors={fieldErrors}
      />

      {/* Footer */}
      <LandingFooter
        scrollToContact={scrollToContact}
        {...(onAdminAccess ? { onAdminAccess } : {})}
      />
    </div>
  )
}

export default LandingPage
